import React, { FunctionComponent, useState, useEffect, useCallback, useMemo } from 'react';
import { Alert, AlertTitle, Button, Grid, Typography } from '@rbx/ui';
import { getCookieValueByKey, setCookie } from '@rbx/core';
import {
  CLOUD_PAGE_IAV1_PATH_COOKIE,
  CLOUD_PAGE_IAV2_OPT_OUT_COOKIE,
} from '@modules/utilities/constants/cloudPageOptOutCookieConstants';
import { useRouter } from 'next/router';
import { useTranslation } from '@rbx/intl';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import DocsSiteSource from '@modules/eventStream/enum/DocsSiteSource';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import useAppMainConfig from '@modules/ui/shared/hooks/useAppMainConfig';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import useAppLayoutMainStyles from '@modules/layout/layout/components/AppLayoutMain.styles';
import { CLOUD_DOCS_HOME_PAGE, isCloudHomePage } from '@modules/ui/shared/utils/cloudPages';
import useCloudIABannerStyles from './CloudIABanner.styles';

const CLOUD_BASE_PATHS = ['/cloud', '/reference/cloud'];

const sanitizePath = (path: string | undefined): string | null => {
  if (!path) {
    return null;
  }

  try {
    // Normalizes the path (e.g. ../)
    const baseUrl = 'https://example.com';
    const url = new URL(decodeURIComponent(path), baseUrl);
    const sanitizedPath = url.pathname;

    if (!CLOUD_BASE_PATHS.some((basePath) => sanitizedPath.startsWith(basePath))) {
      return null;
    }

    return sanitizedPath;
  } catch {
    return null;
  }
};

const CloudIABanner: FunctionComponent = () => {
  const [isOptedOut, setIsOptedOut] = useState<boolean | null>(null);
  const { mainMaxWidth } = useAppMainConfig();
  const { hasLeftNav, open: leftNavIsOpen } = useSideBarContent();
  const { showSecondaryNavigation, enableNewLayout } = useAppConfig();

  const { classes: appLayoutMainClasses } = useAppLayoutMainStyles({
    hasLeftNav,
    leftNavIsOpen,
    showSecondaryNavigation,
    hasRightNav: false,
    mainMaxWidth,
  });
  const { classes } = useCloudIABannerStyles({ enableNewLayout: Boolean(enableNewLayout) });
  const { translate, translateHTML } = useTranslation();
  const router = useRouter();

  const contentStyles = enableNewLayout ? undefined : appLayoutMainClasses?.content;

  useEffect(() => {
    if (!process.env.enableOpenCloudIAV2Pages) {
      return;
    }

    const optOutCookie = getCookieValueByKey(CLOUD_PAGE_IAV2_OPT_OUT_COOKIE.Name);
    setIsOptedOut(optOutCookie !== null);
  }, []);

  const redirectPath = useMemo(() => {
    const IAV1Path = getCookieValueByKey(CLOUD_PAGE_IAV1_PATH_COOKIE.Name);
    const sanitizedPath = sanitizePath(IAV1Path ?? undefined);
    return sanitizedPath || CLOUD_DOCS_HOME_PAGE;
  }, []);

  const handleOptOut = useCallback(() => {
    unifiedLoggerClient.logClickEvent({
      eventName: DocsSiteEventType.OpenCloudIAOptOut,
      parameters: {
        source: DocsSiteSource.OpenCloud,
      },
    });
    setCookie(CLOUD_PAGE_IAV2_OPT_OUT_COOKIE.Name, 'true', CLOUD_PAGE_IAV2_OPT_OUT_COOKIE.Options);
    const currentPath = router.asPath.split('?')[0]; // Remove query params for comparison
    if (isCloudHomePage(redirectPath) && isCloudHomePage(currentPath)) {
      router.reload();
    } else {
      router.push(redirectPath);
    }
  }, [router, redirectPath]);

  const handleOptBackIn = useCallback(() => {
    // Expires the cookies
    unifiedLoggerClient.logClickEvent({
      eventName: DocsSiteEventType.OpenCloudIAOptBackIn,
      parameters: {
        source: DocsSiteSource.OpenCloud,
      },
    });
    setCookie(CLOUD_PAGE_IAV2_OPT_OUT_COOKIE.Name, 'false', {
      path: '/',
      expires: new Date(0).toISOString(),
      'max-age': 0,
    });
    setCookie(CLOUD_PAGE_IAV1_PATH_COOKIE.Name, '', {
      path: '/',
      expires: new Date(0).toISOString(),
      'max-age': 0,
    });
    router.reload();
  }, [router]);

  if (!process.env.enableOpenCloudIAV2Pages) {
    return null;
  }

  // Don't render until we know the cookie state (prevents hydration mismatch)
  if (isOptedOut === null) {
    return null;
  }

  // Show opt-back-in banner if user has opted out
  if (isOptedOut) {
    return (
      <Grid className={contentStyles}>
        <Grid container className={classes.main}>
          <Alert
            severity='info'
            variant='outlined'
            classes={{ root: classes.alert }}
            action={
              <Button color='inherit' size='small' onClick={handleOptBackIn}>
                {translate('Action.OptBackIn')}
              </Button>
            }>
            <AlertTitle classes={{ root: classes.alertTitle }}>
              {translate('Title.OptedOut')}
            </AlertTitle>
            <Typography variant='body2' display='block' classes={{ root: classes.alertText }}>
              {translateHTML('Description.OptBackIn', [
                {
                  opening: 'linkStart',
                  closing: 'linkEnd',
                  // TODO @gxue to replace this link to a devforum post to allow creators to provide feedback
                  // eslint-disable-next-line react/no-unstable-nested-components
                  content: (linkText) => (
                    <DynamicallyRouted.Link
                      href='#'
                      color='inherit'
                      variant='body2'
                      underline='always'>
                      {linkText}
                    </DynamicallyRouted.Link>
                  ),
                },
              ])}
            </Typography>
            <Typography variant='body2' display='block' classes={{ root: classes.alertTextMobile }}>
              {translateHTML('Description.OptInMobile', [
                {
                  opening: 'linkStart',
                  closing: 'linkEnd',
                  // TODO @gxue to replace this link to a devforum post to allow creators to provide feedback
                  // eslint-disable-next-line react/no-unstable-nested-components
                  content: (linkText) => (
                    <DynamicallyRouted.Link
                      href='#'
                      color='inherit'
                      variant='body2'
                      underline='always'>
                      {linkText}
                    </DynamicallyRouted.Link>
                  ),
                },
              ])}
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    );
  }

  // Show opt-out banner if user hasn't opted out (default state)
  return (
    <Grid container className={contentStyles}>
      <Grid container className={classes.main}>
        <Alert
          severity='info'
          variant='outlined'
          classes={{ root: classes.alert }}
          action={
            <Button
              color='inherit'
              size='small'
              onClick={handleOptOut}
              classes={{ root: classes.optOutButton }}>
              {translate('Action.OptOut')}
            </Button>
          }>
          <AlertTitle classes={{ root: classes.alertTitle }}>
            {translate('Title.OptedIn')}
          </AlertTitle>
          <Typography variant='body2' display='block' classes={{ root: classes.alertText }}>
            {translateHTML('Description.OptOut', [
              {
                opening: 'linkStart',
                closing: 'linkEnd',
                // TODO @gxue to replace this link to a devforum post to allow creators to provide feedback
                // eslint-disable-next-line react/no-unstable-nested-components
                content: (linkText) => (
                  <DynamicallyRouted.Link
                    href='#'
                    color='inherit'
                    variant='body2'
                    underline='always'>
                    {linkText}
                  </DynamicallyRouted.Link>
                ),
              },
            ])}
          </Typography>
          <Typography variant='body2' display='block' classes={{ root: classes.alertTextMobile }}>
            {translateHTML('Description.OptOutMobile', [
              {
                opening: 'linkStart',
                closing: 'linkEnd',
                // TODO @gxue to replace this link to a devforum post to allow creators to provide feedback
                // eslint-disable-next-line react/no-unstable-nested-components
                content: (linkText) => (
                  <DynamicallyRouted.Link
                    href='#'
                    color='inherit'
                    variant='body2'
                    underline='always'>
                    {linkText}
                  </DynamicallyRouted.Link>
                ),
              },
            ])}
          </Typography>
        </Alert>
      </Grid>
    </Grid>
  );
};

export default CloudIABanner;
